const SOP_STATE_KEY = "royal_kavery_staff_sop_state"
const SOP_AUDIT_KEY = "royal_kavery_staff_sop_audit"

const safeJsonParse = (value, fallback) => {
    try {
        return value ? JSON.parse(value) : fallback
    } catch {
        return fallback
    }
}

const readLocalStorage = (key, fallback) => {
    if (typeof window === "undefined") return fallback
    return safeJsonParse(window.localStorage.getItem(key), fallback)
}

const writeLocalStorage = (key, value) => {
    if (typeof window === "undefined") return
    try {
        window.localStorage.setItem(key, JSON.stringify(value))
    } catch {
        // Keep SOP working even if storage is unavailable.
    }
}

const decodeTokenPayload = (token) => {
    try {
        if (!token || token.split(".").length < 2) return {}
        return JSON.parse(window.atob(token.split(".")[1]))
    } catch {
        return {}
    }
}

export const getCurrentEmployee = () => {
    const storedUser = readLocalStorage("userDetails", {})
    const tokenPayload = decodeTokenPayload(typeof window !== "undefined" ? window.localStorage.getItem("token") : "")
    const employee = Object.keys(storedUser || {}).length ? storedUser : tokenPayload

    const displayName =
        employee?.name ||
        employee?.fullName ||
        [employee?.firstName, employee?.lastName].filter(Boolean).join(" ") ||
        employee?.userName ||
        employee?.email ||
        "Admin User"

    const email = employee?.email || employee?.userName || "admin@royalkavery.com"
    const employeeId =
        employee?._id ||
        employee?.id ||
        employee?.userId ||
        employee?.sub ||
        employee?.userName ||
        email

    return {
        employeeId: employeeId || "unknown-employee",
        displayName,
        email,
        role: employee?.role || "Staff",
    }
}

const readSopState = () => readLocalStorage(SOP_STATE_KEY, {})

const writeSopState = (value) => writeLocalStorage(SOP_STATE_KEY, value)

export const getEmployeeSopState = (employeeId) => {
    const state = readSopState()
    return state?.[employeeId] || {
        progress: {},
        stats: {
            openCount: 0,
            lastOpenedAt: null,
            lastReviewedAt: null,
        },
    }
}

const appendAuditEntry = (entry) => {
    const current = readLocalStorage(SOP_AUDIT_KEY, [])
    const next = [entry, ...current].slice(0, 100)
    writeLocalStorage(SOP_AUDIT_KEY, next)
    return next
}

const updateEmployeeState = (employee, updater) => {
    const state = readSopState()
    const current = getEmployeeSopState(employee.employeeId)
    const nextEmployeeState = updater(current)
    const nextState = {
        ...state,
        [employee.employeeId]: nextEmployeeState,
    }
    writeSopState(nextState)
    return nextEmployeeState
}

export const trackSopOpen = (employee) => {
    const now = new Date().toISOString()
    const nextEmployeeState = updateEmployeeState(employee, (current) => ({
        ...current,
        stats: {
            ...current.stats,
            openCount: (current?.stats?.openCount || 0) + 1,
            lastOpenedAt: now,
        },
    }))

    appendAuditEntry({
        id: `${employee.employeeId}-opened-${now}`,
        employeeId: employee.employeeId,
        employeeName: employee.displayName,
        employeeEmail: employee.email,
        type: "opened",
        at: now,
    })

    return nextEmployeeState
}

export const trackSopReviewed = (employee) => {
    const now = new Date().toISOString()
    const nextEmployeeState = updateEmployeeState(employee, (current) => ({
        ...current,
        stats: {
            ...current.stats,
            lastReviewedAt: now,
        },
    }))

    appendAuditEntry({
        id: `${employee.employeeId}-reviewed-${now}`,
        employeeId: employee.employeeId,
        employeeName: employee.displayName,
        employeeEmail: employee.email,
        type: "reviewed",
        at: now,
    })

    return nextEmployeeState
}

export const updateSopProgress = (employee, progress) => {
    return updateEmployeeState(employee, (current) => ({
        ...current,
        progress,
    }))
}

export const resetSopProgress = (employee) => {
    return updateEmployeeState(employee, (current) => ({
        ...current,
        progress: {},
    }))
}

export const getSopAuditLog = () => readLocalStorage(SOP_AUDIT_KEY, [])
